import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface BdMermaidProps {
  /** Mermaid の記述 */
  source: string;
  /** 図の読み上げ名 */
  label: string;
}

/** 描いた結果。 @graph-connects none */
type Rendered = { source: string; svg: string } | { source: string; error: string };

/**
 * 基本設計の図（全体図・ER 図）を Mermaid で描く。
 *
 * 描けなかったときは元の記述をそのまま出す。記述は API が行から組み立てたもので、描けない
 * 原因（行の書き方）を読み手が記述から辿れるようにするため。mermaid は重いので、この図が
 * 画面に出たときだけ読み込む。要件定義の図の部品は使わない（あちらの拡大表示や地の色の
 * 都合に引きずられないように、基本設計の図はここだけで描く）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の全体図・ER 図を Mermaid で描き、描けなければ元の記述を出す
 * @graph-connects mermaid [calls] 図の描画
 */
export function BdMermaid({ source, label }: BdMermaidProps) {
  const { t } = useTranslation();
  // mermaid は描いた svg の id を DOM に置くので、同じ画面の 2 枚の図で重ならない id にする
  const baseId = `bd-mermaid-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`;
  const [rendered, setRendered] = useState<Rendered | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const mermaid = (await import('mermaid')).default;
        // ラベルは人が書いた行の文なので、mermaid の strict（ラベルの HTML を無害化し、
        // クリック時のスクリプトを無効にする）で描く
        mermaid.initialize({ startOnLoad: false, theme: 'neutral', securityLevel: 'strict' });
        const { svg } = await mermaid.render(baseId, source);
        if (alive) setRendered({ source, svg });
      } catch (error) {
        if (alive) {
          setRendered({ source, error: error instanceof Error ? error.message : String(error) });
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [baseId, source]);

  // 記述が変わった直後は、前の記述で描いた図を出さない
  const current = rendered?.source === source ? rendered : null;

  if (current === null) {
    return (
      <p className="py-4 text-sm text-[var(--color-text-muted)]">{t('bd.mermaid.rendering')}</p>
    );
  }
  if ('error' in current) {
    return (
      <div
        role="alert"
        className="space-y-2 rounded-lg border border-[var(--color-error)]/30 bg-[var(--color-error-muted)] p-3"
      >
        <p className="text-sm text-[var(--color-error-text)]">{t('bd.mermaid.failed')}</p>
        <pre className="max-h-80 overflow-auto whitespace-pre-wrap font-mono text-xs text-[var(--color-text-secondary)]">
          {source}
        </pre>
      </div>
    );
  }
  return (
    // 地を白にするのは、mermaid の neutral テーマが線と文字をほぼ黒で描くため（暗い面では消える）
    <div
      role="img"
      aria-label={label}
      className="overflow-x-auto rounded-lg border border-[var(--color-border)] bg-white p-3 [&_svg]:mx-auto [&_svg]:h-auto [&_svg]:max-w-full"
      dangerouslySetInnerHTML={{ __html: current.svg }}
    />
  );
}
